import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CookieConsentBanner } from './CookieConsentBanner';
import { CookieConsentSettings } from './CookieConsentSettings';
import { cookieConsentStorage } from './cookie-consent.storage';
import {
  acceptAllConsent,
  rejectAllConsent,
  type CookieConsent,
  type CookieConsentDraft,
} from './cookie-consent.types';
import './cookie-consent.scss';

type View = 'banner' | 'settings';

/**
 * Non-blocking cookie consent card.
 * Renders nothing once a consent decision exists in storage.
 */
export function CookieConsent() {
  const { t } = useTranslation('common');
  const [consent, setConsent] = useState<CookieConsent | null>(() =>
    cookieConsentStorage.get(),
  );
  const [view, setView] = useState<View>('banner');
  const [draft, setDraft] = useState<CookieConsentDraft>(() => ({
    analytics: false,
    marketing: false,
  }));

  if (consent) return null;

  const persist = (next: CookieConsent) => {
    cookieConsentStorage.save(next);
    setConsent(next);
  };

  return (
    <aside className="cookie-consent" aria-live="polite">
      {view === 'banner' ? (
        <CookieConsentBanner
          title={t('cookieConsent.title')}
          body={t('cookieConsent.body')}
          customizeLabel={t('cookieConsent.customize')}
          rejectLabel={t('cookieConsent.rejectAll')}
          acceptLabel={t('cookieConsent.acceptAll')}
          onCustomize={() => {
            setDraft({ analytics: false, marketing: false });
            setView('settings');
          }}
          onRejectAll={() => persist(rejectAllConsent())}
          onAcceptAll={() => persist(acceptAllConsent())}
        />
      ) : (
        <CookieConsentSettings
          title={t('cookieConsent.settingsTitle')}
          necessaryLabel={t('cookieConsent.necessary')}
          necessaryHint={t('cookieConsent.necessaryHint')}
          analyticsLabel={t('cookieConsent.analytics')}
          analyticsHint={t('cookieConsent.analyticsHint')}
          marketingLabel={t('cookieConsent.marketing')}
          marketingHint={t('cookieConsent.marketingHint')}
          alwaysActiveLabel={t('cookieConsent.alwaysActive')}
          backLabel={t('cookieConsent.back')}
          saveLabel={t('cookieConsent.save')}
          draft={draft}
          onChange={setDraft}
          onBack={() => setView('banner')}
          onSave={() => persist(cookieConsentStorage.saveDraft(draft))}
        />
      )}
    </aside>
  );
}

export default CookieConsent;
